import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useRoles, usePermissionCatalogue } from '@/modules/roles/api';
import { useVenues } from '@/modules/venues/venues';
import { Button } from '@/shared/ui/components/button';
import { Label } from '@/shared/ui/components/label';
import { apiErrorMessage } from '@/shared/api-client';
import {
  useCreateRoleAssignment,
  useRevokeRoleAssignment,
  useRoleAssignments,
  useUpdateRoleAssignmentVenueAccess,
  type RoleAssignment,
} from './api';
import { effectivePermissions, permissionAreaIndex } from './effectivePermissions';
import type { PermissionAreaCode } from '@/shared/i18n/locales/en-US/common';

const SELECT_CLASS = 'h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs';

/**
 * The role's permissions that currently take effect, rendered as labelled chips with the
 * VenueAccess note. Both the list rows and the create/edit previews share it so
 * "what this assignment actually grants" reads the same everywhere. An empty result is its own
 * understandable state rather than a blank space.
 */
function hasWorkspacePermissions(
  codes: readonly string[],
  areaIndex: Map<string, PermissionAreaCode>,
): boolean {
  return codes.some((code) => areaIndex.get(code) === 'venue_workspace');
}

function EffectivePermissions({
  codes,
  hasVenueAccess,
}: {
  codes: string[];
  hasVenueAccess: boolean;
}) {
  const { t } = useTranslation('common');
  const labels = t('roles.permissionLabels', { returnObjects: true }) as Record<string, string>;
  return (
    <div className="grid gap-1">
      <p className="text-muted-foreground text-xs font-medium">
        {t('users.assignments.effective.label')}
      </p>
      <p className="text-muted-foreground text-xs">
        {hasVenueAccess
          ? t('users.assignments.effective.withVenueAccess')
          : t('users.assignments.effective.withoutVenueAccess')}
      </p>
      {codes.length === 0 ? (
        <p className="text-muted-foreground text-xs">{t('users.assignments.effective.none')}</p>
      ) : (
        <ul className="flex flex-wrap gap-1">
          {codes.map((code) => (
            <li key={code} className="bg-muted rounded px-1.5 py-0.5 text-xs">
              {labels?.[code] ?? code}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Role-assignment management for the user-detail view (S9). Lists current
 * assignments with role + VenueAccess, and—when the viewer holds the matching
 * `role_assignment.*` permission—lets them add, edit selected Venues, and revoke. The
 * backend 403/409 stays authoritative; this only hides controls and surfaces errors.
 */
export function UserAssignments({ userId }: { userId: string }) {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const canView = auth.can('role_assignment.view');
  const canCreate = auth.can('role_assignment.create');
  const canEdit = auth.can('role_assignment.edit');
  const canRevoke = auth.can('role_assignment.delete');

  const assignments = useRoleAssignments(canView ? userId : null);
  const roles = useRoles();
  const catalogue = usePermissionCatalogue();
  const [adding, setAdding] = useState(false);

  const areaIndex = useMemo(() => permissionAreaIndex(catalogue.data), [catalogue.data]);
  const permsByRole = useMemo(() => {
    const map = new Map<string, string[]>();
    roles.data?.items.forEach((role) => map.set(role.id, role.permissions));
    return map;
  }, [roles.data]);

  if (!canView) return null;

  return (
    <div className="mt-2 grid gap-3 border-t pt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{t('users.assignments.title')}</p>
        {canCreate && !adding && (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            {t('users.assignments.add')}
          </Button>
        )}
      </div>

      {assignments.isLoading && (
        <p className="text-muted-foreground text-sm">{t('users.assignments.loading')}</p>
      )}

      {assignments.data && assignments.data.items.length === 0 && !adding && (
        <p className="text-muted-foreground text-sm">{t('users.assignments.empty')}</p>
      )}

      {assignments.data && assignments.data.items.length > 0 && (
        <ul className="grid gap-2">
          {assignments.data.items.map((assignment) => (
            <AssignmentRow
              key={assignment.id}
              assignment={assignment}
              canEdit={canEdit}
              canRevoke={canRevoke}
              rolePermissions={permsByRole.get(assignment.roleId) ?? []}
              areaIndex={areaIndex}
            />
          ))}
        </ul>
      )}

      {adding && <AddAssignmentForm userId={userId} onDone={() => setAdding(false)} />}
    </div>
  );
}

function AssignmentRow({
  assignment,
  canEdit,
  canRevoke,
  rolePermissions,
  areaIndex,
}: {
  assignment: RoleAssignment;
  canEdit: boolean;
  canRevoke: boolean;
  rolePermissions: string[];
  areaIndex: Map<string, PermissionAreaCode>;
}) {
  const { t } = useTranslation('common');
  const updateVenueAccess = useUpdateRoleAssignmentVenueAccess();
  const revoke = useRevokeRoleAssignment();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const hasEditableVenueAccess = assignment.venueAccess.kind === 'selected';
  const venueAccessLabel =
    assignment.venueAccess.kind === 'admin_implicit_all'
      ? t('users.assignments.venueAccess.adminImplicitAll')
      : assignment.venueAccess.venues.length > 0
        ? assignment.venueAccess.venues.map((venue) => venue.venueName ?? venue.venueId).join(', ')
        : assignment.venueAccess.unresolved
          ? t('users.assignments.venueAccess.unresolved')
          : t('users.assignments.venueAccess.none');
  const hasVenueAccess =
    assignment.venueAccess.kind === 'admin_implicit_all' ||
    assignment.venueAccess.venues.length > 0;

  const effective = useMemo(
    () => effectivePermissions(rolePermissions, hasVenueAccess, areaIndex),
    [rolePermissions, hasVenueAccess, areaIndex],
  );

  const busy = updateVenueAccess.isPending || revoke.isPending;

  const runRevoke = async () => {
    try {
      await revoke.mutateAsync(assignment.id);
      toast.success(t('users.assignments.toast.revoked'));
    } catch (caught) {
      toast.error(apiErrorMessage(t, 'users', caught));
    }
  };

  return (
    <li className="grid gap-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-medium">{assignment.roleName}</span>
          <span className="text-muted-foreground"> · {venueAccessLabel}</span>
        </div>
        <div className="flex gap-2">
          {canEdit && hasEditableVenueAccess && !editing && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setEditing(true)}>
              {t('users.assignments.editVenueAccess')}
            </Button>
          )}
          {canRevoke && !confirming && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
              {t('users.assignments.revoke')}
            </Button>
          )}
        </div>
      </div>

      {!editing && <EffectivePermissions codes={effective} hasVenueAccess={hasVenueAccess} />}

      {editing && (
        <EditVenueAccessForm
          assignment={assignment}
          rolePermissions={rolePermissions}
          areaIndex={areaIndex}
          onDone={() => setEditing(false)}
        />
      )}

      {confirming && (
        <div className="grid gap-2 rounded-md border border-destructive/40 p-2">
          <p className="text-sm">{t('users.assignments.confirmRevoke')}</p>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              disabled={busy}
              onClick={() => void runRevoke()}
            >
              {t('users.assignments.revoke')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              {t('actions.cancel')}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function EditVenueAccessForm({
  assignment,
  rolePermissions,
  areaIndex,
  onDone,
}: {
  assignment: RoleAssignment;
  rolePermissions: string[];
  areaIndex: Map<string, PermissionAreaCode>;
  onDone: () => void;
}) {
  const { t } = useTranslation('common');
  const venues = useVenues();
  const updateVenueAccess = useUpdateRoleAssignmentVenueAccess();
  const [venueIds, setVenueIds] = useState<Set<string>>(
    new Set(
      assignment.venueAccess.kind === 'selected'
        ? assignment.venueAccess.venues.map((venue) => venue.venueId)
        : [],
    ),
  );
  const venueItems = useMemo(() => venues.data?.items ?? [], [venues.data]);
  const workspaceRole = hasWorkspacePermissions(rolePermissions, areaIndex);
  const hasVenueAccess = venueIds.size > 0;

  const effective = useMemo(
    () => effectivePermissions(rolePermissions, hasVenueAccess, areaIndex),
    [rolePermissions, hasVenueAccess, areaIndex],
  );

  const toggleVenue = (id: string) => {
    setVenueIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    try {
      await updateVenueAccess.mutateAsync({
        id: assignment.id,
        venueAccess: { kind: 'selected', venueIds: [...venueIds] },
      });
      toast.success(t('users.assignments.toast.updated'));
      onDone();
    } catch (caught) {
      toast.error(apiErrorMessage(t, 'users', caught));
    }
  };

  return (
    <div className="grid gap-2">
      <Label>{t('users.assignments.venueAccess.label')}</Label>
      {venueItems.length === 0 && (
        <p className="text-muted-foreground text-sm">{t('users.assignments.selectVenues')}</p>
      )}
      {venueItems.map((venue) => (
        <label key={venue.id} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={venueIds.has(venue.id)}
            onChange={() => toggleVenue(venue.id)}
          />
          {venue.name}
        </label>
      ))}
      {workspaceRole && venueIds.size === 0 && (
        <p className="text-destructive text-sm">{t('users.assignments.selectVenues')}</p>
      )}
      <EffectivePermissions codes={effective} hasVenueAccess={hasVenueAccess} />
      <div className="flex gap-2">
        <Button size="sm" disabled={updateVenueAccess.isPending} onClick={() => void save()}>
          {t('users.assignments.saveVenueAccess')}
        </Button>
        <Button variant="outline" size="sm" disabled={updateVenueAccess.isPending} onClick={onDone}>
          {t('actions.cancel')}
        </Button>
      </div>
    </div>
  );
}

function AddAssignmentForm({ userId, onDone }: { userId: string; onDone: () => void }) {
  const { t } = useTranslation('common');
  const roles = useRoles();
  const catalogue = usePermissionCatalogue();
  const venues = useVenues();
  const create = useCreateRoleAssignment(userId);

  const [roleId, setRoleId] = useState('');
  const [venueIds, setVenueIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const venueItems = useMemo(() => venues.data?.items ?? [], [venues.data]);

  // Preview which permissions currently apply for the chosen Role and selected VenueAccess.
  const areaIndex = useMemo(() => permissionAreaIndex(catalogue.data), [catalogue.data]);
  const selectedRole = roles.data?.items.find((role) => role.id === roleId);
  const workspaceRole = hasWorkspacePermissions(selectedRole?.permissions ?? [], areaIndex);
  const hasVenueAccess = venueIds.size > 0;
  const effective = useMemo(
    () => effectivePermissions(selectedRole?.permissions ?? [], hasVenueAccess, areaIndex),
    [selectedRole, hasVenueAccess, areaIndex],
  );

  const toggleVenue = (id: string) => {
    setVenueIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!roleId) {
      setError(t('users.assignments.selectRole'));
      return;
    }
    try {
      await create.mutateAsync({
        roleId,
        venueAccess: { kind: 'selected', venueIds: [...venueIds] },
      });
      toast.success(t('users.assignments.toast.created'));
      onDone();
    } catch (caught) {
      setError(apiErrorMessage(t, 'users', caught));
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 rounded-md border p-3">
      <p className="text-sm font-medium">{t('users.assignments.addTitle')}</p>

      <div className="grid gap-2">
        <Label htmlFor="assign-role">{t('users.assignments.role')}</Label>
        <select
          id="assign-role"
          className={SELECT_CLASS}
          value={roleId}
          onChange={(event) => setRoleId(event.target.value)}
        >
          <option value="" disabled>
            {t('users.assignments.selectRole')}
          </option>
          {roles.data?.items.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-1">
        <Label>{t('users.assignments.venueAccess.label')}</Label>
        {venueItems.length === 0 && (
          <p className="text-muted-foreground text-sm">{t('users.assignments.selectVenues')}</p>
        )}
        {venueItems.map((venue) => (
          <label key={venue.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={venueIds.has(venue.id)}
              onChange={() => toggleVenue(venue.id)}
            />
            {venue.name}
          </label>
        ))}
      </div>

      {roleId && workspaceRole && venueIds.size === 0 && (
        <p className="text-muted-foreground text-sm">
          {t('users.assignments.venueAccess.requiredForWorkspace')}
        </p>
      )}

      {roleId && <EffectivePermissions codes={effective} hasVenueAccess={hasVenueAccess} />}

      {error && <p className="text-destructive text-sm">{error}</p>}

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={create.isPending}>
          {t('users.assignments.submit')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={create.isPending}
          onClick={onDone}
        >
          {t('actions.cancel')}
        </Button>
      </div>
    </form>
  );
}
